import { useState } from 'react';
import Icon from './Icon';
import StatInfo from './StatInfo';
import { lifecycleBadgeClass, fmtApprovedAt } from '../utils/paymentLifecycle';

// The one badge for the supplier-month payment lifecycle, everywhere it shows
// (/suppliers, /supplier/:id header, /suppliers/paid). Renders what the server
// resolved — see utils/paymentLifecycle.js for why nothing is computed here.
//
//   `lifecycle`   { state, label, warning, hint, approved_by, approved_at,
//                   approval_available } from the API; null/undefined → '—'.
//                 On a multi-month window `hint` may read 'אושרו X מתוך Y חודשים'.
// Hints are NEVER table text (owner, 2026-09-04): 'ללא אישור' on a paid row
// and 'אושרו X מתוך Y חודשים' on a partly approved window are facts worth
// knowing on hover, not labels to scan for — so they ride as the badge's
// title (desktop only by nature; a phone gets the label alone). The partial
// case is also spelled out in the approve panel's 'אשר את שאר החודשים'.
const HINT_UNAPPROVED = 'ללא אישור';
const UNAPPROVED_TIP = 'שולם ללא אישור לתשלום - התשלום תועד בלי שהחודש אושר קודם';
const hintTip = (hint) => (hint === HINT_UNAPPROVED ? UNAPPROVED_TIP : hint);

export function PaymentLifecycleBadge({ lifecycle }) {
  if (!lifecycle || !lifecycle.state) return <span style={{ color: 'var(--ink-subtle)' }}>—</span>;
  return (
    <span
      className="lifecycle-badge"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span
        className={`badge ${lifecycleBadgeClass(lifecycle.state)}`}
        title={lifecycle.hint ? hintTip(lifecycle.hint) : undefined}
        aria-label={lifecycle.hint ? `${lifecycle.label} · ${lifecycle.hint}` : undefined}
      >{lifecycle.label}</span>
      {lifecycle.warning && (
        // Amber, not red: approving with a לא-תקין document is allowed — the
        // mark says "look", not "stop" (warn, never block).
        <span role="img" aria-label={lifecycle.warning} title={lifecycle.warning}
          style={{ color: 'var(--warning-ink)', display: 'inline-flex', lineHeight: 1 }}>
          <Icon name="alert" size={14} />
        </span>
      )}
    </span>
  );
}

// The badge as the row's approve/cancel control. Same look; when the user may
// act it becomes a button that opens a small panel with the approver line, the
// לא-תקין warning (with the explicit "you may still approve"), and the single
// action. Uses the row-⋮ overlay pattern this app already has (fixed backdrop
// + absolute panel — not a portal, and not clipped by .table-wrapper).
//
//   `canApprove`  the caller's role check (manager+); viewers get the plain badge
//   `busy`        an approve is in flight — the item is disabled, never double-fired
//   `onToggle`    () => void; the caller does the API call and swaps the row
export function PaymentLifecycleControl({ lifecycle, canApprove, busy, onToggle }) {
  const [open, setOpen] = useState(false);
  if (!canApprove) return <PaymentLifecycleBadge lifecycle={lifecycle} />;

  const approved = Boolean(lifecycle?.approved_by);
  // A partly approved window (some of its months) completes on the next click.
  const partial = !approved && Number(lifecycle?.approved_months) > 0;
  const prompt = approved ? 'מאושר לתשלום — לחצי לביטול האישור' : 'לחצי לאישור לתשלום';
  return (
    <span style={{ position: 'relative', display: 'inline-block' }} onClick={e => e.stopPropagation()}>
      {/* The badge alone reads as a label, and a reviewer could not find the
          approve action at all — so the button carries a chevron and a
          tooltip that say it opens something. */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={prompt}
        title={prompt}
        onClick={() => setOpen(o => !o)}
        className="lifecycle-control"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 2 }}
      >
        <PaymentLifecycleBadge lifecycle={lifecycle} />
        <span aria-hidden="true" style={{ color: 'var(--ink-muted)', display: 'inline-flex', lineHeight: 1 }}>
          <Icon name="chevron" size={14} />
        </span>
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="page-menu-panel" role="menu" style={{ minWidth: 230 }}>
            {approved && (
              <div style={{ padding: '6px 12px 8px', fontSize: '0.8rem', color: 'var(--ink-muted)', borderBottom: '1px solid var(--hairline)', marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>אושר ע״י</div>
                <div style={{ color: 'var(--ink-muted)' }}>{lifecycle.approved_by}{lifecycle.approved_at ? ` · ${fmtApprovedAt(lifecycle.approved_at)}` : ''}</div>
              </div>
            )}
            {lifecycle?.invalid_docs > 0 && (
              <div style={{ padding: '6px 12px 8px', fontSize: '0.8rem', color: 'var(--warning-ink)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <Icon name="alert" size={14} />
                <span>
                  {lifecycle.invalid_docs === 1 ? 'מסמך אחד לא תקין' : `${lifecycle.invalid_docs} מסמכים לא תקינים`}
                  {!approved && ' — ניתן לאשר בכל זאת'}
                </span>
              </div>
            )}
            <button
              type="button"
              role="menuitem"
              className="page-menu-item"
              disabled={busy}
              style={approved ? { color: 'var(--danger-ink)' } : { color: 'var(--success-ink)', fontWeight: 600 }}
              onClick={() => { setOpen(false); onToggle(); }}
            >
              <Icon name={approved ? 'x-circle' : 'check-circle'} size={16} />
              {approved ? 'בטל אישור' : partial ? 'אשר את שאר החודשים' : 'אשר לתשלום'}
            </button>
          </div>
        </>
      )}
    </span>
  );
}

export default PaymentLifecycleBadge;

// The column's ⓘ — what each state means, in the reader's words, and how to
// approve. Wording signed off by the owner (2026-09-04); one copy for every
// header that shows the lifecycle, so the two payment tabs cannot drift.
export const LIFECYCLE_INFO = (
  <>
    <strong>מצב תשלום - איפה הספק בתהליך התשלום החודש</strong>
    <ul className="stat-info-list">
      <li><strong>דורש בדיקה</strong> - יש מסמך עם בעיה (זיכוי פתוח או חשבונית מרכזת שלא מסתכמת).</li>
      <li><strong>ממתין לאישור</strong> - המסמכים תואמים, אבל עדיין לא אישרתם לשלם.</li>
      <li><strong>מאושר לתשלום</strong> - אישרתם. שומרים מי ומתי.</li>
      <li><strong>שולם חלקית</strong> - שולם חלק מהסכום.</li>
      <li><strong>שולם</strong> - הכול שולם. אם החודש לא אושר לפני התיעוד, מעבר עם העכבר על הסטטוס יראה "ללא אישור".</li>
    </ul>
    <p className="stat-info-note">
      <strong>לאישור:</strong> לוחצים על הסטטוס בשורה ובוחרים "אשר לתשלום" (זמין לתפקיד מנהל בלבד).
    </p>
  </>
);

export function LifecycleHelp() {
  return <StatInfo label="מצב תשלום" maxWidth={340}>{LIFECYCLE_INFO}</StatInfo>;
}
